/**
 * AssistantReportCard — cartão de download do relatório gerado pelo @Atendente.
 *
 * O @Atendente chamou a tool `generate_report`, que persistiu o relatório e
 * propôs uma ação `download_report` (report_id + filename). Este card renderiza
 * um botão que baixa o PDF (layout Amazonas Inovare) via axios — que injeta o
 * Bearer token; uma âncora <a href> nativa iria SEM o header Authorization → 401.
 *
 * O filename vem do header Content-Disposition (padrão espelhado de
 * frontend/src/api/waf.ts:getWafDashboardPdf).
 */
import { useState } from 'react'
import { Button, Card, Group, Text } from '@mantine/core'
import { IconFileTypePdf } from '@tabler/icons-react'
import { api } from '@/api/client'

interface Props {
  reportId: string
  filename?: string
}

export function AssistantReportCard({ reportId, filename }: Props) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download() {
    setLoading(true)
    setError(null)
    try {
      const r = await api.get(`/v1/assistant/reports/${reportId}/pdf`, {
        responseType: 'blob',
        timeout: 60000,
      })
      const cd = (r.headers?.['content-disposition'] as string | undefined) ?? ''
      const match = /filename="?([^"]+)"?/.exec(cd)
      const name = match?.[1] ?? filename ?? `relatorio-${reportId}.pdf`
      const blob = r.data as Blob
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
      setError('Não foi possível baixar o relatório. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        borderLeft: '3px solid var(--mantine-color-teal-6, #0d9488)',
        maxWidth: 480,
      }}
    >
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconFileTypePdf size={18} color="var(--mantine-color-teal-6, #0d9488)" />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          Relatório em PDF disponível
        </Text>
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        Gerado no layout Amazonas Inovare.
      </Text>
      <Button
        size="xs"
        color="inovareTeal"
        variant="filled"
        loading={loading}
        onClick={download}
        leftSection={<IconFileTypePdf size={14} />}
      >
        Baixar relatório (PDF)
      </Button>
      {error && (
        <Text size="xs" c="red" mt="xs">
          {error}
        </Text>
      )}
    </Card>
  )
}
